// Lightweight tracing for the route transition, so a hard-to-reproduce freeze can be captured after the
// fact. Keeps a rolling buffer of recent events and exposes `window.__gtDump()` — when the UI wedges, run
// that in the devtools console and share the output: it prints the recent event log plus the live DOM state
// (each layer's content tag, transform, z-index, height) so we can see exactly what's covering what.
//
// Console output is gated on `window.__gtDebug` (default ON in dev). The buffer + dump always work.

interface GtEvent {
  n: number
  t: number
  evt: string
  data?: Record<string, unknown>
}

type GtWindow = Record<string, unknown>
const gtWin = (): GtWindow => window as unknown as GtWindow

const BUFFER: GtEvent[] = []
const MAX = 120
let seq = 0
/** Every mounted transition surface (root element → its scope name), in mount order. */
const ROOTS = new Map<HTMLElement, string>()

const translateX = (el: HTMLElement): number => Math.round(new DOMMatrixReadOnly(getComputedStyle(el).transform).m41)

export function gtlog(evt: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  const e: GtEvent = { n: ++seq, t: Math.round(performance.now()), evt, data }
  BUFFER.push(e)
  if (BUFFER.length > MAX) BUFFER.shift()
  // The BUFFER always records (it's what `__gtDump()` and the e2e spec read — the cancel spec asserts on this
  // event sequence, so this is load-bearing, not scaffolding). Console echo is opt-in: `window.__gtDebug = true`.
  if (gtWin()['__gtDebug'] === true) {
    // eslint-disable-next-line no-console
    console.debug('[GT]', e.n, evt, data ? JSON.stringify(data) : '')
  }
}

/**
 * Content fingerprint of a route pane, so the log reads a heading or BLANK rather than raw HTML — and so the
 * outlet can tell a page that rendered nothing (still suspending) from one that has content.
 *
 * Deliberately never touches `innerText`. That was the single most expensive function in the whole transition
 * (8% of all busy CPU): `innerText` is layout-dependent, so reading it forces a synchronous layout flush AND
 * serialises every rendered character of the page into a string — an admin table's worth of garbage — and it
 * ran on every navigation, on every surface. `childElementCount` answers "is this blank?" in O(1) without
 * touching layout, and a single heading lookup gives the log something human to read.
 */
export function contentTag(el: HTMLElement | null | undefined): string {
  if (!el) return 'BLANK'
  if (el.childElementCount === 0 && !el.textContent?.trim()) return 'BLANK'
  const heading = el.querySelector('h1,h2,h3')?.textContent?.trim()
  return heading ? heading.slice(0, 18) : 'OK'
}

/**
 * Register a transition surface with `window.__gtDump()` (call it from the console when the UI wedges) and
 * return the deregister. Surfaces NEST (the root outlet, plus one inside the admin console for its tabs), so
 * the dump reports every mounted one — with a single-root dump the inner surface silently replaced the outer
 * and you'd be reading the wrong stage's state while chasing a freeze.
 */
export function gtInstallDump(root: HTMLElement, scope: string): () => void {
  if (typeof window === 'undefined') return () => {}
  const w = gtWin()
  ROOTS.set(root, scope)
  w['__gtDump'] = () => {
    const surfaces = Array.from(ROOTS.entries()).map(([el, name]) => ({
      surface: name,
      state: el.dataset.gtState ?? '(unknown)',
      snaps: el.dataset.gtSnaps ?? '?',
      rootOverflow: el.style.overflow || '(none)',
      children: Array.from(el.children).map((child, i) => {
        const h = child as HTMLElement
        return {
          i,
          layer: h.getAttribute('data-gt-layer') ?? (h.hasAttribute('data-gt-shadow') ? 'SHADOW' : 'LIVE'),
          tag: contentTag(h),
          x: translateX(h),
          z: getComputedStyle(h).zIndex,
          h: Math.round(h.getBoundingClientRect().height),
          pe: getComputedStyle(h).pointerEvents,
        }
      }),
    }))
    const dump = { url: location.pathname + location.search, surfaces, recentLog: BUFFER.slice(-60) }
    // eslint-disable-next-line no-console
    console.log('[GT] DUMP\n' + JSON.stringify(dump, null, 2))
    return dump
  }
  return () => {
    ROOTS.delete(root)
  }
}
